import React, { useState, useEffect } from "react";
import "./reports_company.css";
import Sidebar from "../../components/Sidebar";
import { useSelector } from "react-redux";
import {
  getBankStatementsListApi,
  getExpenseListApi,
  getMileageListApi,
  getSalesListApi,
} from "../../services/apiCall";
import { usePagination } from "../../hooks/usePagination";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";

const tabs = ["Sales", "Expenses", "Mileage", "Bank"];

export default function ReportsCompanyPage() {
  const [activeTab, setActiveTab] = useState("Expenses");
  const businessId = useSelector((state) => state?.user?.businessId);

  // Define fetch functions
  const fetchExpensesFn = React.useCallback(async () => {
    if (!businessId) return [];
    const res = await getExpenseListApi({ businessId });
    return res?.data?.expenses || [];
  }, [businessId]);

  const fetchSalesFn = React.useCallback(async () => {
    if (!businessId) return [];
    const res = await getSalesListApi({ businessId });
    return res?.data?.sales || [];
  }, [businessId]);

  const fetchMileageFn = React.useCallback(async () => {
    if (!businessId) return [];
    const res = await getMileageListApi({ businessId });
    return res?.data?.mileage || [];
  }, [businessId]);

  const fetchBankFn = React.useCallback(async () => {
    if (!businessId) return [];
    const res = await getBankStatementsListApi({ businessId });
    return res?.data?.statements || [];
  }, [businessId]);

  // Initialize pagination hooks
  const expensesPag = usePagination(fetchExpensesFn, {
    dependencies: [businessId],
  });
  const salesPag = usePagination(fetchSalesFn, { dependencies: [businessId] });
  const mileagePag = usePagination(fetchMileageFn, {
    dependencies: [businessId],
  });
  const bankPag = usePagination(fetchBankFn, { dependencies: [businessId] });

  // Get current pagination based on active tab
  const getCurrentPagination = () => {
    switch (activeTab) {
      case "Expenses":
        return expensesPag;
      case "Sales":
        return salesPag;
      case "Mileage":
        return mileagePag;
      case "Bank":
        return bankPag;
      default:
        return expensesPag;
    }
  };

  const {
    displayedData: activeData,
    loading,
    loadMore,
    hasMore,
  } = getCurrentPagination();

  // Infinite Scroll Ref
  const lastElementRef = useInfiniteScroll(loading, hasMore, loadMore);

  const renderRow = (item) => {
    switch (activeTab) {
      case "Expenses":
        return (
          <div className="report-item" key={item.id}>
            <div className="item-left">
              <h4>{item.expenseCategory?.name || "Unknown Category"}</h4>
              <p>{item.description || "-"}</p>
              <p>{new Date(item.createdAt).toLocaleDateString("en-GB")}</p>
            </div>
            <div className="item-right">
              <span className="amount">
                ${parseFloat(item.gross || 0).toFixed(2)}
              </span>
              <p className="vat">
                VAT: ${parseFloat(item.vat || 0).toFixed(2)}
              </p>
            </div>
          </div>
        );

      case "Sales":
        return (
          <div className="report-item" key={item.id}>
            <div className="item-left">
              <h4>{item.invoiceNumber || "No Invoice"}</h4>
              <p>Gross: ${parseFloat(item.gross || 0).toFixed(2)}</p>
              <p>{new Date(item.createdAt).toLocaleDateString("en-GB")}</p>
            </div>
            <div className="item-right">
              <span className="amount">
                ${parseFloat(item.net || 0).toFixed(2)}
              </span>
              <p className="vat">
                VAT: ${parseFloat(item.vat || 0).toFixed(2)}
              </p>
            </div>
          </div>
        );

      case "Mileage":
        return (
          <div className="report-item" key={item.id}>
            <div className="item-left">
              <h4>{item.description}</h4>
              <p>
                {item.from} → {item.to}
              </p>
              <p>{item.reason}</p>
            </div>
            <span className="amount">{item.miles} miles</span>
          </div>
        );

      case "Bank":
        return (
          <div className="report-item" key={item.id}>
            <div className="item-left">
              <h4>{item.description || "No Description"}</h4>
              <p>
                {item.category} •{" "}
                <span
                  className={
                    item.transactionType === "CREDIT" ? "credit" : "debit"
                  }
                >
                  {item.transactionType}
                </span>
              </p>
              <p>
                {new Date(item.transactionDate).toLocaleDateString("en-GB")}
              </p>
            </div>
            <div className="bank-right">
              <span
                className="amount"
                style={{
                  color:
                    item.transactionType === "CREDIT" ? "#2ecc71" : "#e74c3c",
                }}
              >
                {item.transactionType === "DEBIT" ? "-$" : "+$"}
                {parseFloat(item.amount || 0).toFixed(2)}
              </span>
              <p className="balance">
                Bal: ${parseFloat(item.balance || 0).toFixed(2)}
              </p>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="app">
      <Sidebar />

      <main className="main">
        <header className="page-header">
          <h1 className="page-title">Reports</h1>
        </header>

        <div className="tab-bar">
          {tabs.map((tab) => (
            <button
              key={tab}
              className={`tab-btn ${activeTab === tab ? "active" : ""}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab}
            </button>
          ))}
        </div>

        <section className="report-card">
          {loading && activeData.length === 0 ? (
            <div style={{ textAlign: "center", padding: "20px" }}>
              Loading...
            </div>
          ) : activeData.length > 0 ? (
            <>
              <div className="report-list">
                {activeData.map((item, index) => (
                  <div
                    key={item.id}
                    className="report-item show"
                    style={{
                      transitionDelay: `${index * 50}ms`,
                    }}
                  >
                    {renderRow(item)?.props?.children || renderRow(item)}
                  </div>
                ))}
              </div>

              {hasMore && (
                <div
                  ref={lastElementRef}
                  style={{
                    textAlign: "center",
                    marginTop: "20px",
                    marginBottom: "20px",
                  }}
                >
                  {loading ? (
                    <span style={{ color: "#888" }}>Loading...</span>
                  ) : (
                    <span style={{ color: "#888" }}>Scroll for more</span>
                  )}
                </div>
              )}
            </>
          ) : (
            <p className="empty-state">No {activeTab.toLowerCase()} found</p>
          )}
        </section>
      </main>
    </div>
  );
}
